import type { AxiosInstance } from 'axios';
import type { DocumentTemplate, CreateTemplateRequest, UpdateTemplateRequest, TemplatePlaceholder, TemplateSigningField, PlaceholderScanResult } from '../types/template';
import type { PaginatedResponse, PaginationParams } from '../types/common';

export function createTemplateService(client: AxiosInstance) {
  return {
    create: (data: CreateTemplateRequest) =>
      client.post<DocumentTemplate>('/v1/templates', data).then(r => r.data),

    getAll: (params: PaginationParams & { status?: string }) =>
      client.get<PaginatedResponse<DocumentTemplate>>('/v1/templates', { params }).then(r => r.data),

    getById: (id: string) =>
      client.get<DocumentTemplate>(`/v1/templates/${id}`).then(r => r.data),

    update: (id: string, data: UpdateTemplateRequest) =>
      client.put<DocumentTemplate>(`/v1/templates/${id}`, data).then(r => r.data),

    delete: (id: string) =>
      client.delete(`/v1/templates/${id}`),

    scanPlaceholders: (id: string) =>
      client.post<PlaceholderScanResult>(`/v1/templates/${id}/scan-placeholders`).then(r => r.data),

    forceSave: (docKey: string) =>
      client.post(`/v1/onlyoffice/forcesave`, null, { params: { key: docKey } }),

    getDataPlaceholders: (id: string) =>
      client.get<TemplatePlaceholder[]>(`/v1/templates/${id}/data-placeholders`).then(r => r.data),

    upsertDataPlaceholders: (id: string, placeholders: TemplatePlaceholder[]) =>
      client.post<TemplatePlaceholder[]>(`/v1/templates/${id}/data-placeholders`, placeholders).then(r => r.data),

    getSigningFields: (id: string) =>
      client.get<TemplateSigningField[]>(`/v1/templates/${id}/signing-fields`).then(r => r.data),

    upsertSigningFields: (id: string, fields: TemplateSigningField[]) =>
      client.post<TemplateSigningField[]>(`/v1/templates/${id}/signing-fields`, fields).then(r => r.data),

    deleteSigningField: (templateId: string, fieldId: string) =>
      client.delete(`/v1/templates/${templateId}/signing-fields/${fieldId}`),

    preview: (id: string) =>
      client.get<Blob>(`/v1/templates/${id}/preview`, { responseType: 'blob' }),

    generatePdf: (id: string, placeholderValues: Record<string, string>) =>
      client.post<Blob>(`/v1/templates/${id}/generate-pdf`, placeholderValues, { responseType: 'blob' }),

    submit: (id: string) =>
      client.post<DocumentTemplate>(`/v1/templates/${id}/submit`).then(r => r.data),

    approve: (id: string) =>
      client.post<DocumentTemplate>(`/v1/templates/${id}/approve`).then(r => r.data),

    deactivate: (id: string, reason: string) =>
      client.post<DocumentTemplate>(`/v1/templates/${id}/deactivate`, { reason }).then(r => r.data),

    search: (filters: Record<string, unknown>, params: PaginationParams) =>
      client.post<PaginatedResponse<DocumentTemplate>>('/v1/templates/search', filters, { params }).then(r => r.data),
  };
}
